import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Sparkles, Heart, Activity, Newspaper, Settings, ChevronRight, X,
} from 'lucide-react'
import Modal from './ui/Modal'
import { WELCOME_SLIDES } from '../lib/welcomeSlides'

// Icon-name → lucide component map. Mirrors the TipCard / TourStep
// pattern so the slide catalogue stays UI-free.
const ICONS = { Sparkles, Heart, Activity, Newspaper, Settings }

// One-time welcome carousel. Fires on first cold launch (after the
// onboarding wizard) before the spotlight tour. Paginated slides
// with an icon + title + body. "Skip" closes early and marks both
// the carousel AND the tour as seen so an opt-out user isn't
// re-prompted on the next launch. "Get started" closes the carousel
// and the controller hands off to the spotlight tour.
export default function WelcomeCarousel({ onSkip, onStart }) {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  const slide = WELCOME_SLIDES[index]
  const isLast = index >= WELCOME_SLIDES.length - 1
  const Icon = (slide && ICONS[slide.icon]) || Sparkles

  const next = () => {
    if (isLast) onStart?.()
    else setIndex((i) => Math.min(WELCOME_SLIDES.length - 1, i + 1))
  }

  if (!slide) return null

  return (
    <Modal open onClose={onSkip} ariaLabel={t('welcome.modalAria')}>
      <div className="flex justify-end mb-2">
        <button
          type="button"
          onClick={onSkip}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col items-center text-center px-2 pb-2 space-y-4">
        <span className="p-4 rounded-2xl bg-app-primary-500/10 text-app-primary-500">
          <Icon className="w-10 h-10" />
        </span>
        <h2 className="text-xl font-bold text-foreground" dir="auto">
          {t(slide.titleKey)}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed" dir="auto">
          {t(slide.bodyKey)}
        </p>
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-3" dir="ltr">
        {WELCOME_SLIDES.map((_, i) => (
          <span
            key={i}
            className={i === index
              ? 'w-6 h-1.5 rounded-full bg-app-primary-500 transition-all'
              : 'w-1.5 h-1.5 rounded-full bg-app-primary-500/30 transition-all'}
          />
        ))}
      </div>

      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 py-2 rounded-md border-2 border-border text-sm text-foreground hover:bg-accent"
          dir="auto"
        >
          {t('welcome.skip')}
        </button>
        <button
          type="button"
          onClick={next}
          className="flex-1 py-2 rounded-md bg-app-primary-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1"
          dir="auto"
        >
          <span>{isLast ? t('welcome.getStarted') : t('welcome.next')}</span>
          <ChevronRight className="w-4 h-4 rtl:-scale-x-100" />
        </button>
      </div>
    </Modal>
  )
}
